import { memo, useEffect, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import { cn } from "~/utils/tailwind";

export interface ITextBoxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  name: string;
  label?: string;
  type?: string;
  disabled?: boolean;
  hint?: string;
  className?: string;
  maxLength?: any;
  isRequired?: boolean;
  showLabel?: boolean;
  hidden?: boolean;
  autoComplete?: any;
  onFocus?: any;
  showErrorMessage?: boolean;
  showAutocomplete?: boolean;
  autocompleteOptions?: any;
  onOptionSelect?: any;
  title?: string;
}

const TextBoxField = ({
  id,
  name,
  label,
  type,
  disabled,
  hint,
  className,
  isRequired = false,
  maxLength,
  showLabel = true,
  hidden,
  autoComplete,
  onFocus,
  showErrorMessage = true,
  showAutocomplete = false,
  autocompleteOptions = [],
  onOptionSelect,
  title = "",
  ...props
}: ITextBoxProps) => {
  const [focused, setFocused] = useState<boolean>(false);
  const { control } = useFormContext();
  const {
    field: { value, ref, ...inputProps },
    fieldState,
  } = useController({ control, name, defaultValue: "" });

  const handleFocus = (e: any) => {
    setFocused(true);
    if (onFocus != null) {
      onFocus(e);
    }
  };

  useEffect(() => {
    return () => {
      setFocused(false);
    };
  }, []);

  return (
    <div
      className={cn(
        showAutocomplete ? "" : "relative",
        "group",
        "has-[input.parent-fw]:col-span-2"
      )}
      hidden={hidden}
    >
      {showLabel && (
        <label
          htmlFor={name}
          className="absolute -top-2 left-2 inline-block bg-neutral-100 px-1 text-[0.875rem] font-medium text-neutral-600"
        >
          <span className={cn(fieldState.error && "text-red-700")}>
            {label} {isRequired && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}
      <input
        {...props}
        type={type}
        id={id ?? name}
        title={title}
        className={cn(
          "block w-full h-12 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
          className,
          fieldState.error && "border-red-700 border-solid border-2"
        )}
        //  placeholder={label ?? ""}
        maxLength={maxLength}
        disabled={disabled}
        autoComplete={autoComplete}
        onFocus={handleFocus}
        value={value === null ? "" : value}
        ref={ref}
        {...inputProps}
        // onBlur={handleBlur}
      />
      {fieldState?.error?.message && showErrorMessage && (
        <span className="text-red-700 px-1 text-xs">
          {fieldState?.error?.message}
        </span>
      )}
      {hint && <span className="px-2 text-xs">{hint}</span>}
      {focused && showAutocomplete && value !== null && value.length > 0 && (
        <ul className="bg-neutral-100 absolute w-40 z-50">
          {autocompleteOptions
            .filter((opt: any) =>
              opt.label.toLowerCase().startsWith(value.toLowerCase())
            )
            .map((opt: any, index: number) => {
              return (
                <li
                  key={index}
                  className="hover:bg-red-300"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOptionSelect({ name, option: opt });
                    setFocused(false);
                  }}
                >
                  {opt.label}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
};

export default memo(TextBoxField);
